import { useEffect, useRef, useState } from "react";
import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../../../utils/listenDatabaseValue";
import { db } from "../../../lib/firebase";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { startRefreshGoogleSearchTerms } from "../../../actions/adsCampaigns";
import { GOOGLE_SEARCH_TERMS_CACHE_TTL_MS } from "../../../utils/constants";

const isSearchTermsCacheFresh = (cachedAt) => {
  if (!cachedAt) return false;
  return Date.now() - Number(cachedAt) < GOOGLE_SEARCH_TERMS_CACHE_TTL_MS;
};

const useGoogleAdGroupSearchTerms = ({
  enabled,
  idPlatform,
  integrationID,
  adAccountID,
}) => {
  const dispatch = useDispatchApp();
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const [searchTerms, setSearchTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const refreshAttemptedRef = useRef("");

  useEffect(() => {
    if (!enabled || !idPlatform || !advertiserID) {
      setSearchTerms([]);
      setLoading(false);
      return;
    }

    const idPlatformFormatted = String(idPlatform);
    const cacheKey = `${advertiserID}/${idPlatformFormatted}`;
    refreshAttemptedRef.current = "";
    let isMounted = true;

    const dbRef = ref(
      db,
      `adsGroupsSearchTerms/${advertiserID}/${idPlatformFormatted}`
    );

    const unsubscribe = onValue(dbRef, (snapshot) => {
      if (!isMounted) return;

      const value = snapshot.val();
      const cachedTerms = Array.isArray(value?.searchTerms)
        ? value.searchTerms
        : [];
      const hasCache = Boolean(value?.cachedAt);

      if (hasCache) {
        setSearchTerms(cachedTerms);
        setLoading(false);
      }

      const shouldRefresh =
        !isSearchTermsCacheFresh(value?.cachedAt) &&
        refreshAttemptedRef.current !== cacheKey &&
        integrationID &&
        adAccountID;

      if (!shouldRefresh) {
        if (!hasCache) {
          setSearchTerms([]);
          setLoading(false);
        }
        return;
      }

      refreshAttemptedRef.current = cacheKey;
      if (!hasCache) {
        setLoading(true);
      }

      dispatch(
        startRefreshGoogleSearchTerms({
          idPlatform: idPlatformFormatted,
          integrationID,
          adAccountID,
        })
      ).then((response) => {
        if (!isMounted) return;
        if (!response?.ok && !hasCache) {
          setLoading(false);
        }
      });
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [enabled, idPlatform, advertiserID, integrationID, adAccountID, dispatch]);

  return { searchTerms, loading };
};

export default useGoogleAdGroupSearchTerms;
